import { endOfDay, isAfter, isBefore, startOfDay } from 'date-fns';
import { AutocompleteRenderInputParams, FormLabel, Typography } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import { IMaskMixin } from 'react-imask';
import { useState } from 'react';

import { Checkbox } from '@components/checkbox';
import { Switch } from '@components/switch/Switch';
import { RadioGroupField } from '@components/radioButton';
import { TFormField, FormFieldType } from '@models/forms';
import { useFormStore } from '@components/forms';
import { DateTimeRangeField } from '@components/forms/fields/DateTimeRangeField';
import { DatePicker, datePickerStyles } from '@components/datePicker/DatePicker';
import { TextField } from '@components/textField/TextField';
import { FormControlLabel } from '@components/formControlLabel/FormControlLabel';
import { Item, Select } from '@components/select/Select';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { InfoCircle, Phone } from '@assets/svgs';
import { getUndefinedIfEmpty } from '@utils/getUndefinedIfEmpty';
import { convertUnicodeToAscii, isInvalidAscii } from '@utils/convertUnicodeToAscii';
import { Autocomplete } from '@components/autocomplete/Autocomplete';
import { TypeAheadFilter } from '@components/table/filters/TypeAheadFilter';
import { Field } from '@pages/NewQuote/scope';
import { Controller } from 'react-hook-form';
import { RichTextEditor } from '@components/richTextEditor/RichTextBase';
import { Box } from '@mui/system';

export type FieldComponentProps = {
  field: TFormField;
  forceDisabled: boolean;
};

const IMaskPhoneInput = IMaskMixin(({ ...props }: any) => {
  return <TextField {...props} />;
});

export const FieldComponent = ({ field, forceDisabled }: FieldComponentProps) => {
  const { control, formState, setError, clearErrors, watch, getValues } = useFormStore((state) => state);

  const defaultHasStoppedTyping =
    field.fieldType === FormFieldType.TextField && field.fieldConfig?.textField?.regexPattern && field.defaultValue
      ? true
      : false;
  const [userHasStoppedTyping, setUserHasStoppedTyping] = useState(defaultHasStoppedTyping);

  const watchedField = field.watchedField ? watch(field.watchedField) : undefined;
  const watchedFields = field.watchedFields
    ? field.watchedFields.reduce(
        (acc, curr) => {
          acc[curr] = watch(curr);
          return acc;
        },
        {} as { [key: string]: any },
      )
    : undefined;

  const isRequired =
    typeof field.required === 'boolean'
      ? field.required
      : typeof field.required === 'function'
        ? field.required({ data: watchedFields ? watchedFields : watchedField })
        : false;

  const isDisabled =
    typeof field.disabled === 'boolean'
      ? field.disabled
      : typeof field.disabled === 'function'
        ? field.disabled({ data: { watchedField, ...watchedFields } })
        : false;

  const validator = {
    required: isRequired,
  };

  const customErrorKey: string = `${field.name}-custom`;
  //** Used to 'flag' the field until its debounced handler is called */
  const loadingErrorKey: string = `${field.name}-loading`;

  const handleDebouncedError = useDebouncedCallback(
    (field, error) => {
      // using a custom debounce here so the error message doesn't enable as soon as you type in the textbox
      setError(customErrorKey, { message: error, type: 'regex' });
      setUserHasStoppedTyping(true);
    },
    userHasStoppedTyping ? 0 : DEBOUNCE_DELAYS.FORM_TYPING,
  );

  const handleDebouncedInputChange = useDebouncedCallback((field, value, reason) => {
    if (field?.fieldConfig?.selectField?.handleInputChange) {
      field.fieldConfig.selectField.handleInputChange(value, reason);
    }
    if (field?.fieldConfig?.textField?.handleDebouncedInputChange) {
      field.fieldConfig.textField.handleDebouncedInputChange(value);
      clearErrors(loadingErrorKey);
    }
  }, DEBOUNCE_DELAYS.FORM_TYPING);

  // Handle nested field names like '24232.value' by properly accessing nested errors
  const getFieldError = (fieldName: string) => {
    if (fieldName.includes('.')) {
      const fieldPath = fieldName.split('.');
      let error = formState.errors;
      for (const path of fieldPath) {
        error = error?.[path];
        if (!error) break;
      }
      return error?.message;
    }
    return formState.errors[fieldName]?.message;
  };

  const formStateErrors = getFieldError(field.name);
  const customError = getFieldError(customErrorKey);
  const hoverText = field?.hoverText;

  const hasRichTextContent = (value: unknown): boolean => {
    if (typeof value !== 'string') {
      return false;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return false;
    }

    try {
      const parsed = JSON.parse(trimmedValue) as { blocks?: { text?: string }[] };
      if (!Array.isArray(parsed?.blocks)) {
        return trimmedValue.length > 0;
      }

      return parsed.blocks.some((block) => (block?.text ?? '').trim().length > 0);
    } catch {
      return trimmedValue.length > 0;
    }
  };

  switch (field.fieldType) {
    case FormFieldType.DateTimeRangeField: {
      return <DateTimeRangeField field={field} forceDisabled={forceDisabled} />;
    }
    case FormFieldType.DatePickerField: {
      const dateConfig = field?.fieldConfig?.dateField;

      return (
        <Controller
          defaultValue={field.defaultValue}
          name={field.name}
          control={control}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              value={value ? (typeof value === 'string' ? new Date(value) : value) : undefined}
              sx={{ ...datePickerStyles, ...field.sx }}
              fullWidth
              autoFocus={field.autoFocus}
              onChange={onChange}
              disabled={forceDisabled || isDisabled}
              openTo={dateConfig?.openTo}
              minDate={dateConfig?.minDate ? new Date(dateConfig?.minDate) : undefined}
              maxDate={dateConfig?.maxDate ? new Date(dateConfig?.maxDate) : undefined}
              textFieldProps={{
                tooltipTitle: hoverText ?? field.tooltipTitle,
                inputLabelIcon: hoverText ? <InfoCircle style={{ flexShrink: 0 }} /> : null,
                inputLabel: field.label,
                required: isRequired,
                error: !!formStateErrors || field.error,
                helperLabel: formStateErrors ?? field.helperText,
                config: { allowHelperLabelWrap: true },
                slotProps: {
                  htmlInput: {
                    variant: field.variant,
                    'data-testid': `date-picker-field-${field.name}`,
                  },
                },
              }}
            />
          )}
          rules={{
            required: isRequired ?? false,
            validate: (value) => {
              if (dateConfig?.minDate && isBefore(value, startOfDay(new Date(dateConfig?.minDate)))) {
                return 'Date is before minimum date';
              }
              if (dateConfig?.maxDate && isAfter(value, endOfDay(new Date(dateConfig?.maxDate)))) {
                return 'Date is after maximum date';
              }
              return true;
            },
          }}
        />
      );
    }

    case FormFieldType.PhoneNumberField: {
      return (
        <Controller
          rules={{
            ...validator,
            required: isRequired ?? false,
            validate: (value) => {
              if (!value && !isRequired) {
                return true;
              }
              const phoneRegex = /\+1\s\(\d{3}\)\s\d{3}-\d{4}/;

              if (!phoneRegex.test(value)) {
                const error = 'The phone number is not valid';
                handleDebouncedError(field, error);
                return false;
              }

              handleDebouncedError.cancel();
              if (formState.errors[customErrorKey]) {
                clearErrors(customErrorKey);
              }

              return true;
            },
          }}
          control={control}
          name={field.name}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value } }) => {
            const hasError = !!customError || !!formStateErrors || !!field.error;
            return (
              <>
                <FormLabel>
                  <Typography variant='caption' color={hasError ? 'error.main' : 'text.caption'}>
                    {field.label}
                    {field.label && field.required && ' *'}
                  </Typography>
                </FormLabel>

                <IMaskPhoneInput
                  autoFocus
                  value={value}
                  aria-label={field.name}
                  fullWidth
                  mask='+{1} (000) 000-0000'
                  placeholder={field.placeholder}
                  color='primary'
                  onAccept={(newValue: string) => onChange(newValue)}
                  error={hasError}
                  helperText={customError ?? formStateErrors ?? field.helperText}
                  startIcon={<Phone />}
                  data-testid={`phone-number-field-${field.name}`}
                  name={field.name}
                />
              </>
            );
          }}
        />
      );
    }

    case FormFieldType.TextField: {
      const textConfig = field?.fieldConfig?.textField;

      return (
        <Controller
          rules={{
            ...validator,
            maxLength: textConfig?.maxLength,
            validate: (value) => {
              if (textConfig?.regexPattern) {
                if (!textConfig.regexPattern.test(value)) {
                  const error = textConfig?.regexMessage ?? 'The value is not valid';
                  handleDebouncedError(field, error);
                } else {
                  handleDebouncedError.cancel();
                  clearErrors(customErrorKey);
                }
              }
              if (textConfig?.handleDebouncedInputChange) {
                setError(loadingErrorKey, { message: 'loading', type: 'loading' });
                handleDebouncedInputChange(field, value, '');
              }

              if (field.fieldConfig?.textField?.inputType === 'number') {
                return true;
              }

              const invalidAsciiMessage = isInvalidAscii(value);
              if (invalidAsciiMessage) {
                return invalidAsciiMessage;
              }

              return true;
            },
          }}
          control={control}
          name={field.name}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value } }) => {
            const lengthHelperText = textConfig?.maxLength ? `${value?.length ?? 0}/${textConfig.maxLength}` : '';
            const rightAlign = !customError && !formStateErrors && !field.helperText;

            const formattedCustomError = getUndefinedIfEmpty<string>(customError);
            const formattedFormStateErrors = getUndefinedIfEmpty<string>(formStateErrors);

            return (
              <TextField
                fullWidth
                startIcon={textConfig?.startIcon}
                endIcon={textConfig?.endIcon}
                value={value}
                data-testid={`text-field-${field.name}`}
                inputLabel={field.label}
                required={isRequired}
                type={textConfig?.inputType}
                placeholder={field.placeholder}
                helperText={formattedCustomError ?? formattedFormStateErrors ?? field.helperText ?? lengthHelperText}
                error={!!customError || !!formStateErrors || !!field.error}
                autoFocus={field.autoFocus}
                autoComplete={textConfig?.autocomplete}
                disabled={forceDisabled || isDisabled}
                name={field.name}
                onChange={(event) => {
                  if (field.fieldConfig?.textField?.inputType === 'number') {
                    onChange(textConfig?.handleOnPattern ? textConfig.handleOnPattern(event.target.value) : event);
                    return;
                  }

                  // convert to ascii
                  const value = event.target.value ? convertUnicodeToAscii(event.target.value) : '';
                  event.target.value = value;

                  onChange(textConfig?.handleOnPattern ? textConfig.handleOnPattern(value) : event);
                }}
                multiline={textConfig?.multiline}
                rows={textConfig?.rows}
                slotProps={{
                  htmlInput: {
                    maxLength: textConfig?.maxLength,
                  },
                  formHelperText: {
                    sx: { textAlign: rightAlign ? 'right' : '' },
                  },
                }}
                tooltipTitle={hoverText}
                inputLabelIcon={hoverText ? <InfoCircle style={{ flexShrink: 0 }} /> : null}
              />
            );
          }}
        />
      );
    }

    case FormFieldType.RichTextEditor: {
      return (
        <Controller
          rules={{
            ...validator,
            validate: (value) => {
              if (!isRequired) {
                return true;
              }

              return hasRichTextContent(value);
            },
          }}
          control={control}
          name={field.name}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value } }) => (
            <Box>
              <Typography variant='caption' sx={{ marginBottom: '-10px' }}>
                {field.label}
              </Typography>
              <RichTextEditor
                isDisabled={field.disabled || forceDisabled ? true : false}
                value={value}
                setContextValue={(v: unknown) => {
                  onChange(v);
                }}
              />
            </Box>
          )}
        />
      );
    }

    case FormFieldType.SelectField: {
      const selectConfig = field?.fieldConfig?.selectField;

      return (
        <Controller
          rules={{
            ...validator,
            validate: (value) => {
              const selectedValue = selectConfig?.options?.find((option) => option.value === value);
              if (selectedValue?.disabled) {
                return false;
              }

              if (selectConfig?.validate) {
                return selectConfig.validate(value, getValues());
              }

              return true;
            },
          }}
          control={control}
          name={field.name}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value } }) => (
            <Select
              fullWidth
              value={value}
              data-testid={`select-field-${field.name}`}
              inputLabel={field.label}
              required={isRequired}
              placeholder={field.placeholder}
              error={!!formStateErrors || !!field.error}
              helperLabel={customError ?? formStateErrors ?? field.helperText}
              autoFocus={field.autoFocus}
              disabled={forceDisabled || isDisabled}
              name={field.name}
              items={selectConfig?.options ?? []}
              sx={field.sx}
              onChange={onChange}
              MenuProps={selectConfig?.menuProps}
              tooltipTitle={hoverText}
              inputLabelIcon={hoverText ? <InfoCircle style={{ flexShrink: 0 }} /> : null}
            />
          )}
        />
      );
    }

    case FormFieldType.FilteredSelectField: {
      const selectConfig = field?.fieldConfig?.selectField;

      return (
        <Controller
          rules={{
            ...validator,
            validate: (value) => {
              const selectedValue = selectConfig?.options?.find((option) => option.value === value);
              return !selectedValue?.disabled;
            },
          }}
          control={control}
          name={field.name}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              fullWidth
              value={value}
              data-testid={`autocomplete-field-${field.name}`}
              autoFocus={field.autoFocus}
              disabled={forceDisabled || isDisabled}
              options={selectConfig?.options ?? []}
              sx={field.sx}
              onInputChange={(_, value, reason) => {
                if (selectConfig?.handleInputChange) {
                  handleDebouncedInputChange(field, value, reason);
                }
              }}
              onChange={(_, value) => onChange(value)}
              renderOption={(props, option) =>
                (option as Item)?.value === '' || (option as Item)?.hidden ? null : (
                  <Box component='li' {...props} key={(option as Item).value} data-testid={option.value}>
                    <Typography
                      variant='bodySmall'
                      sx={{
                        color: 'common.black',
                      }}
                    >
                      {(option as Item).label}
                      {(option as Item).secondaryLabel && (
                        <Typography
                          sx={{
                            wordBreak: 'break-all',
                          }}
                          variant='caption'
                          fontWeight='normal'
                          display='block'
                          color='text.subdued'
                        >
                          {(option as Item).secondaryLabel}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                )
              }
              renderInput={(params: AutocompleteRenderInputParams) => {
                return (
                  <TextField
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                      },
                    }}
                    inputLabel={field.label}
                    required={field.required ? true : false}
                    placeholder={field.placeholder}
                    tooltipTitle={hoverText}
                    inputLabelIcon={hoverText ? <InfoCircle style={{ flexShrink: 0 }} /> : null}
                    {...params}
                  />
                );
              }}
              slotProps={{
                listbox: {
                  onScroll: (e) => selectConfig?.handleOnScroll?.(e),
                  style: {
                    maxHeight: '300px',
                    backgroundColor: 'white',
                  },
                },
              }}
            />
          )}
        />
      );
    }

    case FormFieldType.RadioGroupField: {
      const radioConfig = field?.fieldConfig?.radioField;

      return (
        <Controller
          rules={validator}
          control={control}
          name={field.name}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value } }) => (
            <RadioGroupField
              layout={field?.fieldConfig?.radioField?.layout}
              value={value ?? ''}
              data-testid={`radio-group-field-${field.name}`}
              inputLabel={field.label}
              required={isRequired}
              autoFocus={field.autoFocus}
              disabled={forceDisabled || isDisabled}
              name={field.name}
              options={radioConfig?.options ?? []}
              sx={field.sx}
              sxProps={
                field?.fieldConfig?.radioField?.layout === 'vertical'
                  ? {
                      label: { width: 'fit-content' },
                    }
                  : undefined
              }
              onChange={onChange}
              hoverText={hoverText}
              helperText={customError ?? formStateErrors ?? field.helperText}
            />
          )}
        />
      );
    }

    case FormFieldType.CheckboxField:
      return (
        <Controller
          rules={validator}
          control={control}
          name={field.name}
          defaultValue={field.defaultValue}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              label={
                field.fieldConfig?.checkboxField?.CustomLabelComponent ? (
                  field.fieldConfig?.checkboxField?.CustomLabelComponent
                ) : (
                  <Typography
                    variant='bodySmall'
                    sx={{
                      color: 'text.main',
                      position: 'relative',
                      marginLeft: '34px', // width of checkbox
                      fontWeight: field?.fieldConfig?.checkboxField?.fontWeight ?? 'bold',
                    }}
                  >
                    {field.label}
                  </Typography>
                )
              }
              checked={!!value}
              required={isRequired}
              autoFocus={field.autoFocus}
              disabled={forceDisabled || isDisabled}
              name={field.name}
              sx={{ fontSize: 0, ...field.sx, position: 'relative' }}
              control={
                <Checkbox
                  sx={
                    field.fieldConfig?.checkboxField?.CustomLabelComponent
                      ? null
                      : { position: 'absolute', top: '-7px' }
                  }
                  checked={!!value}
                  data-testid={`checkbox-field-${field.name}`}
                  onChange={(_, checked) => onChange(checked)}
                />
              }
            />
          )}
        />
      );

    case FormFieldType.ToggleSwitchField:
      return (
        <Controller
          rules={validator}
          control={control}
          name={field.name}
          defaultValue={field.defaultValue}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              label={field.label}
              labelPlacement='start'
              required={isRequired}
              autoFocus={field.autoFocus}
              disabled={forceDisabled || isDisabled}
              name={field.name}
              sx={field.sx}
              sxProps={{ formControlLabel: { justifyContent: 'space-between', width: '100%' } }}
              typographyProps={{ sx: { fontWeight: 'normal' } }}
              control={
                <Switch
                  size={field.fieldConfig?.switchField?.size ?? 'small'}
                  variant={field.fieldConfig?.switchField?.variant ?? 'primary'}
                  checked={!!value}
                  onChange={(e) => onChange(e)}
                />
              }
            />
          )}
        />
      );

    case FormFieldType.MultiSelectField: {
      const multiSelectConfig = field?.fieldConfig?.multiSelectField;

      return (
        <Controller
          rules={{
            ...validator,
          }}
          control={control}
          name={field.name}
          defaultValue={field.defaultValue || []}
          render={({ field: { onChange, value } }) => {
            return (
              <TypeAheadFilter
                label={field.label}
                required={isRequired}
                disabled={forceDisabled || isDisabled}
                error={!!formStateErrors || !!field.error}
                helperText={(formStateErrors as string) ?? (field.helperText as string)}
                isMultiple
                options={multiSelectConfig?.options ?? []}
                limitTags={multiSelectConfig?.limitTags ?? 2}
                defaultValue={value}
                value={value ?? []}
                onChange={(newValue) => onChange(newValue ?? [])}
                onInputChange={multiSelectConfig?.onInputChange}
                loading={multiSelectConfig?.loading}
              />
            );
          }}
        />
      );
    }

    case FormFieldType.CustomField: {
      const customFieldComponent = field?.fieldConfig?.customField?.Component;
      const Component = customFieldComponent!;

      return (
        <Component
          field={field}
          required={isRequired}
          disabled={forceDisabled || isDisabled}
          helperText={customError ?? formStateErrors ?? field.helperText}
          error={!!customError || !!formStateErrors || !!field.error}
        />
      );
    }

    case FormFieldType.Label: {
      const value = getValues()?.[field.name];

      const displayValue = typeof value === 'string' ? value : value?.label;

      return (
        <Field
          value={field.label}
          label={displayValue ?? field.defaultValue ?? ''}
          sxProps={field?.fieldConfig?.labelField?.sxProps}
        />
      );
    }
    default:
      return <div>COMPONENT NOT IMPLEMENTED!</div>;
  }
};
